<!--
  Baut aus einer Feldkonfiguration einen vollständigen Abschnitt mit Raster, Zeilen und Eingaben
  v-model ist die Liste der Items, deren valueEdited die Controls direkt beschreiben
  -> Anordnung
-->
<template>
  <SidebarSection :title="header" :collapsedOnMount="collapsedOnMount">
    <SidebarFieldGrid :columns="columns">
      <div v-for="hinweis in hinweise" :key="`hinweis-${hinweis.label}`" class="sidebar-field-section__hint">
        <TriangleAlert :size="16" />
        <span>{{ hinweis.hint }}</span>
      </div>
      <SidebarFieldRow
        v-for="zelle in zellen"
        :key="`zelle-${zelle.key}`"
        :label="zelle.item ? zeilenLabel(zelle.item) : ''"
        :icon="zelle.item ? zelle.item.icon : null"
        :column="zelle.column"
        :full="zelle.full"
        :split="!!(zelle.item && (zelle.item.split || zelle.item.halbeWertespalte))"
        :last="zelle.last"
        :readonly="!zelle.item || istReadonly(zelle.item)"
        :changed="!!zelle.item && istGeaendert(zelle.item)"
        :saisonwert="zelle.item ? saisonwert(zelle.item) : null"
      >
        <template v-if="zelle.item && zelle.item.splitN" #label>
          <span>{{ zelle.item.label }}</span>
          <AddRemoveButton v-if="zelle.item.addable" @click="addSplitNEntry(zelle.item)" />
          <AddRemoveButton
            v-if="zelle.item.addable && zelle.item.valueEdited.length > 1"
            remove
            @click="removeSplitNEntry(zelle.item)"
          />
        </template>

        <template v-if="zelle.item && zelle.item.splitN">
          <SidebarFieldControl
            v-for="i in zelle.item.valueEdited.length"
            :key="`splitn-${i}`"
            :item="zelle.item"
            :index="i - 1"
            :country="country"
            @input="onInput"
          />
        </template>
        <template v-else-if="zelle.item && zelle.item.split">
          <SidebarFieldControl
            :item="zelle.item"
            :index="0"
            :country="country"
            :class="{ 'sidebar-field-section__half': !istReadonly(zelle.item) }"
            @input="onInput"
          />
          <span v-if="istReadonly(zelle.item)" class="sidebar-field-section__separator">/</span>
          <SidebarFieldControl
            :item="zelle.item"
            :index="1"
            :country="country"
            :class="{ 'sidebar-field-section__half': !istReadonly(zelle.item) }"
            @input="onInput"
          />
        </template>
        <SidebarFieldControl v-else-if="zelle.item" :item="zelle.item" :country="country" @input="onInput" />
      </SidebarFieldRow>
    </SidebarFieldGrid>
  </SidebarSection>
</template>

<script>
import { TriangleAlert } from 'lucide-vue';
import { arraysEqualUnordered } from '@/core/common/helpers/utils';
import AddRemoveButton from '@/components/common/add-remove-button.vue';
import SidebarSection from './sidebar-section.vue';
import SidebarFieldGrid from './sidebar-field-grid.vue';
import SidebarFieldRow from './sidebar-field-row.vue';
import SidebarFieldControl from './sidebar-field-control.vue';

export default {
  name: 'SidebarFieldSection',
  components: {
    SidebarSection,
    SidebarFieldGrid,
    SidebarFieldRow,
    SidebarFieldControl,
    TriangleAlert,
    AddRemoveButton,
  },
  props: {
    value: { type: Array, default: () => [] },
    header: { type: String, required: true },
    collapsedOnMount: { type: Boolean, default: false },
    country: { type: String, default: null },
    columns: { type: Number, default: 2 },
  },
  computed: {
    sichtbareItems() {
      return (this.value || []).filter(item => !item?.notShow);
    },
    // Hinweise stehen als eigene Zeile über dem Feldraster, damit die Spaltenpaarung erhalten bleibt
    hinweise() {
      return this.sichtbareItems.filter(item => item?.hint);
    },
    // Die Feldkonfiguration ist spaltenweise sortiert, das Raster füllt zeilenweise
    // Deshalb werden die Items je Spalte gesammelt und anschließend zeilenweise verschränkt
    zellen() {
      const spalten = Array.from({ length: this.columns }, () => []);
      const vollzeilen = [];
      this.sichtbareItems.forEach(item => {
        if (item.full) {
          vollzeilen.push(item);
        } else {
          spalten[Math.min((item.column ?? 1) - 1, this.columns - 1)].push(item);
        }
      });

      const zeilenzahl = Math.max(0, ...spalten.map(spalte => spalte.length));
      const zellen = [];
      for (let zeile = 0; zeile < zeilenzahl; zeile++) {
        for (let spalte = 0; spalte < this.columns; spalte++) {
          const item = spalten[spalte][zeile];
          // Fehlt in einer Spalte ein Item, hält eine leere Zelle das Raster ausgerichtet
          zellen.push({
            item: item ?? null,
            column: spalte + 1,
            full: false,
            key: item ? this.itemKey(item) : `leer-${zeile}-${spalte}`,
          });
        }
      }
      vollzeilen.forEach(item => zellen.push({ item, column: 1, full: true, key: this.itemKey(item) }));

      // Die Zellen der untersten Rasterzeile tragen keine Trennlinie und runden die Kartenecken mit
      if (vollzeilen.length) {
        zellen[zellen.length - 1].last = true;
      } else {
        zellen.slice(-this.columns).forEach(zelle => {
          zelle.last = true;
        });
      }

      return zellen;
    },
  },
  methods: {
    itemKey(item) {
      return Array.isArray(item.label) ? item.label.join('-') : item.label;
    },
    zeilenLabel(item) {
      return Array.isArray(item.label) ? item.label.join(' / ') : item.label;
    },
    // Mehrwertige Felder gelten als editierbar, sobald mindestens ein Teilwert editierbar ist
    istReadonly(item) {
      if (item.splitN) {
        return false;
      }
      return Array.isArray(item.editable) ? !item.editable.some(Boolean) : !item.editable;
    },
    // Vergleicht den bearbeiteten Wert gegen den Serverstand, bei mehrwertigen Feldern je Teilwert
    // Der lose Vergleich ist gewollt, weil Zahlenfelder ihren Wert als String zurückliefern
    istGeaendert(item) {
      if (item.splitN) {
        return (
          item.value.length !== item.valueEdited.length ||
          item.value.some((wert, i) => wert != item.valueEdited[i])
        );
      }
      if (item.split) {
        return [0, 1].some(i => {
          if (!item.editable?.[i]) {
            return false;
          }
          if (Array.isArray(item.value?.[i]) || Array.isArray(item.valueEdited?.[i])) {
            return !arraysEqualUnordered(item.value?.[i], item.valueEdited?.[i]);
          }
          return item.value?.[i] != item.valueEdited?.[i];
        });
      }
      if (!item.editable) {
        return false;
      }
      // Die Flughäfen werden als Array bearbeitet, der Serverstand ist ein kommaseparierter String
      if (item.editType === 'flughafen') {
        return (
          Array.isArray(item.valueEdited) &&
          item.valueEdited.length > 0 &&
          item.valueEdited.join(', ') !== (item.value || '')
        );
      }
      // Listenfelder wie Weitere Partner gelten nur bei anderem Inhalt als geändert, nicht bei anderer Reihenfolge
      if (Array.isArray(item.value) || Array.isArray(item.valueEdited)) {
        return !arraysEqualUnordered(item.value ?? [], item.valueEdited ?? []);
      }
      return item.value != item.valueEdited;
    },
    // Markiert Felder, deren Wert vom Saisonwert der Reise abweicht
    saisonwert(item) {
      const abweichung = item.valueDiffersSeason;
      if (!abweichung || (Array.isArray(abweichung) && !abweichung.some(Boolean))) {
        return null;
      }
      return Array.isArray(item.valueSeason) ? item.valueSeason.join(' / ') : item.valueSeason;
    },
    // valueEdited, valueDiffersSeason und editable werden parallel gehalten,
    // deshalb wächst und schrumpft ein splitN-Feld immer in allen drei Arrays gleichzeitig
    addSplitNEntry(item) {
      item.valueEdited.push(null);
      item.valueDiffersSeason.push(false);
      item.editable.push(true);
    },
    removeSplitNEntry(item) {
      if (item.valueEdited.length > 1) {
        item.valueEdited.pop();
        item.valueDiffersSeason.pop();
        item.editable.pop();
      }
    },
    onInput() {
      this.$emit('input', this.value);
    },
  },
};
</script>

<style scoped>
.sidebar-field-section__hint {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  font-size: 13px;
  color: var(--restriktion-keine-abreise-text);
  background-color: var(--restriktion-keine-abreise-bg);
}
.sidebar-field-section__separator {
  flex: 0 0 auto;
  color: var(--text-subtle);
}
.sidebar-field-section__half {
  flex: 1 1 0;
  min-width: 0;
  justify-content: flex-end;
}
</style>
